import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Calendar } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import api from '../api';

import { FILE_BASE_URL } from '../constants';

const resolveImageSrc = (value) => {
  if (!value) return '';
  if (value.startsWith('http')) return value;
  return `${FILE_BASE_URL}${value}`;
};

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isCreateMode = !id || id === 'create';

  const [formData, setFormData] = useState({
    title: '',
    image: '',
    summary: '',
    content: '',
    date: new Date(),
    author: '',
    status: 'Published'
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const previewSrc = imagePreview || resolveImageSrc(formData.image);

  useEffect(() => {
    if (!isCreateMode && id) {
      const fetchNews = async () => {
        try {
          const response = await api.get(`/news/${id}`);
          const newsData = response.data;
          if (newsData.date) {
            newsData.date = new Date(newsData.date);
          }
          setFormData(newsData);
          setImagePreview(resolveImageSrc(newsData.image));
        } catch (error) {
          console.error("Error fetching news:", error);
        }
      };
      fetchNews();
    } else {
      setFormData({
        title: '',
        image: '',
        summary: '',
        content: '',
        date: new Date(),
        author: '',
        status: 'Published'
      });
      setImageFile(null);
      setImagePreview('');
    }
  }, [id, isCreateMode]);

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      date: date
    }));
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content: content
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImageFile(file);
    setImagePreview(prev => {
      if (prev && prev.startsWith('blob:')) {
        URL.revokeObjectURL(prev);
      }
      if (file) {
        return URL.createObjectURL(file);
      }
      return resolveImageSrc(formData.image);
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        date: formData.date ? formData.date.toISOString().split('T')[0] : ''
      };
      const multipart = new FormData();
      multipart.append('news', JSON.stringify(payload));
      if (imageFile instanceof File) {
        multipart.append('image', imageFile);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      if (isCreateMode) {
        await api.post('/news', multipart, config);
      } else {
        await api.put(`/news/${id}`, multipart, config);
      }
      navigate('/news');
    } catch (error) {
      console.error("Error saving news:", error);
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  return (
    <div className="job-detail">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/news')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="page-title">{isCreateMode ? 'Thêm tin tức mới' : 'Chỉnh sửa tin tức'}</h1>
        </div>
        <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
          <Save size={18} /> {loading ? 'Đang lưu...' : 'Lưu lại'}
        </button>
      </div>

      <div className="form-section">
        <h3 className="section-title">Thông tin chung</h3>
        <div className="form-group">
          <label>Tiêu đề tin tức</label>
          <input
            type="text"
            className="form-input"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Nhập tiêu đề"
          />
        </div>

        <div className="form-group">
          <label>Ảnh bìa</label>
          <input
            type="file"
            className="form-input"
            accept="image/*"
            onChange={handleFileChange}
          />
          {previewSrc && (
            <div style={{ marginTop: '8px' }}>
              <img src={previewSrc} alt="Preview" style={{ height: '100px', borderRadius: '4px' }} />
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Tóm tắt</label>
          <textarea
            className="form-input"
            name="summary"
            style={{ minHeight: '80px', resize: 'vertical' }}
            value={formData.summary}
            onChange={handleChange}
            placeholder="Nhập tóm tắt"
          />
        </div>

        <div className="form-grid-3">
          <div className="form-group">
            <label>Ngày đăng</label>
            <div className="date-input-wrapper">
              <DatePicker
                selected={formData.date}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                className="form-input"
                placeholderText="dd/mm/yyyy"
              />
              <Calendar className="input-icon-right" size={18} style={{ pointerEvents: 'none' }} />
            </div>
          </div>
          <div className="form-group">
            <label>Tác giả</label>
            <input
              type="text"
              className="form-input"
              name="author"
              value={formData.author}
              onChange={handleChange}
              placeholder="Nhập tên tác giả"
            />
          </div>
          <div className="form-group">
            <label>Trạng thái</label>
            <select
              className="form-select"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="Published">Đã xuất bản</option>
              <option value="Draft">Bản nháp</option>
              <option value="Archived">Lưu trữ</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3 className="section-title">Nội dung chi tiết</h3>
        <div className="editor-placeholder">
          <ReactQuill
            theme="snow"
            value={formData.content}
            onChange={handleContentChange}
            modules={modules}
            formats={formats}
            className="editor-content"
          />
        </div>
      </div>

      <style>{`
        .job-detail {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .form-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          border: 1px solid #E0E0E0;
        }
        .section-title {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 16px;
          color: #1A1A1A;
        }
        .form-group {
          margin-bottom: 16px;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #1A1A1A;
        }
        .form-input, .form-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #E0E0E0;
          border-radius: 6px;
          font-size: 14px;
          background: #F5F7FA;
          box-sizing: border-box;
        }
        .form-grid-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .date-input-wrapper {
          position: relative;
        }
        .date-input-wrapper .react-datepicker-wrapper {
            width: 100%;
        }
        .input-icon-right {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #666;
          z-index: 1;
        }
        .editor-placeholder {
          border: 1px solid #E0E0E0;
          border-radius: 6px;
          overflow: hidden;
          background: white;
        }
        .editor-content .ql-container {
            min-height: 300px;
            font-size: 14px;
        }
        .editor-content .ql-editor {
            min-height: 300px;
        }
      `}</style>
    </div>
  );
};

export default NewsDetail;
